import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { LocationEditModal } from "@/pages/settings/locations/widgets/LocationEditModal.tsx";
import { useLocationDelete, useLocations } from "@/queries/use-locations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiLocation } from "@/types/location.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Locations() {
  const { t } = useTranslation();
  const { renderLocationAddress } = useFormatter();

  useTitle(t("core:locations"), t("common:settings"));

  const { openModal } = useModals();

  const { data: locations, isLoading, isFetching } = useLocations();
  const locationDelete = useLocationDelete();

  const columns = useMemo<ColumnDef<ApiLocation>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:address"),
        cell: (row) => (
          <TableCell variant={"secondary"} className={"whitespace-pre-wrap"}>
            {renderLocationAddress(row.row.original)}
          </TableCell>
        ),
        accessorKey: "address",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:timezone"),
        cell: (row) => <TableCell variant={"secondary"}>{row.row.original.timezone}</TableCell>,
        accessorKey: "timezone",
        ...ColumnSizes.standard,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-right px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [locations]
  );

  const handleEditOrCreate = useCallback((location?: ApiLocation) => {
    openModal(LocationEditModal, { location });
  }, []);

  const handleDeleteAction = useCallback((location: ApiLocation) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await locationDelete.mutateAsync(location.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.locations.deleted, {
                [analyticProperties.id]: location.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.locations.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiLocation>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("common:locations")}
          navigateTo={"/settings/company-structure"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={locations || []}
            columns={columns}
            isFetching={isFetching || locationDelete.isPending}
            showSkeleton={isLoading}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
